import { useEffect, useState } from "react";
import { toDataURL } from "qrcode";

/**
 * Draw a string as a QR code, in this browser.
 *
 * Nothing is sent anywhere to be drawn. That started as a rule about the TOTP
 * enrolment URI, which carries a live secret, and it holds for the clinic
 * poster too: a QR service would receive a list of which clinics exist and
 * when each one printed a poster.
 *
 * `onReady` hands back the data URL, so a caller that wants to offer the image
 * as a download does not encode it a second time.
 */
export function QrCode({
  value,
  alt = "QR code for your authenticator app",
  width = 180,
  onReady,
}: {
  value: string;
  alt?: string;
  width?: number;
  onReady?: (dataUrl: string) => void;
}) {
  const [image, setImage] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    // Error correction "H" survives a poster that has been printed, taped to a
    // wall and partly obscured, which is the only place this code is ever
    // read. It is what clinic_qr.py uses for the same reason.
    toDataURL(value, { margin: 1, width, errorCorrectionLevel: "H" })
      .then((url) => {
        if (cancelled) return;
        setImage(url);
        onReady?.(url);
      })
      .catch(() => {
        // The code printed below is the fallback, so a failure here is not
        // worth an error message of its own.
        if (!cancelled) setImage(null);
      });

    return () => {
      cancelled = true;
    };
  }, [value, width, onReady]);

  if (!image) return null;
  return <img className="qr" src={image} alt={alt} width={width} height={width} />;
}
